import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  fetchCustomers,
  fetchEmployees,
  postWorkOrder,
  fetchPoolByCustomer,
  updateTask,
  updateWorkOrder,
  fetchTasksByDate,
  fetchAllWorkOrders,
  applyTemplate,
  applyRoutes,
  reorderRoute,
} from "../../Utils/api";
import { useAuth } from "../../Auth/AuthContext";
import PopupGeneric from "../../Components/PopupGeneric/pop-up-generic";
import CreateWorkOrderModal from "../../Components/CreateWorkOrderModal/CreateWorkOrderModal";
import SaveTemplateModal from "../../Components/SaveTemplateModal/SaveTemplateModal";
import TemplateLibraryModal from "../../Components/TemplateLibraryModal/TemplateLibraryModal";
import "./employee-scheduling.css";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// A "Not Started" task / work order whose due date is older than this many days is
// treated as a stale orphan and hidden from the active schedule.
// IMPORTANT: keep this in sync with STALE_TASK_DAYS in the backend
// (ScheduleTemplateServiceImpl.java) so saved templates match what this screen shows.
const STALE_TASK_DAYS = 14;

// Droppable Container Component
const DroppableContainer = ({ id, children }) => {
  const { setNodeRef } = useDroppable({ id });

  return (
    <div ref={setNodeRef} style={{ minHeight: "100%" }}>
      {children}
    </div>
  );
};

// Draggable Customer Card Component
const DraggableCustomerCard = ({
  customer,
  isActive,
  onRemove,
  onAdd,
  showEmployeeName,
  showDay,
  isDragEnabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: customer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition, // use dnd-kit's own transition (~150ms) instead of overriding with 500ms
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={{ ...style, marginBottom: "10px" }} {...attributes}>
      <Card
        className={`customer-card ${isDragging ? "dragging" : ""}`}
        sx={{}}
      >
        <CardContent sx={{ padding: "12px !important" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              sx={{
                flexGrow: 1,
                cursor: isDragEnabled ? "grab" : "default",
                userSelect: "none",
                "&:active": isDragEnabled ? { cursor: "grabbing" } : {},
                "& *": { cursor: isDragEnabled ? "grab" : "default", userSelect: "none" },
                "&:active *": isDragEnabled ? { cursor: "grabbing" } : {},
              }}
              {...(isDragEnabled ? listeners : {})}
              onDoubleClick={() => {
                if (!isActive && isDragEnabled && onAdd) {
                  onAdd(customer);
                }
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                {customer.name}
              </Typography>
              {isActive && showDay && (
                <Typography variant="body2" color="text.secondary">
                  {customer.dayLabel}
                </Typography>
              )}
              {isActive && showEmployeeName && (
                <Typography variant="body2" color="text.secondary">
                  {customer.employeeName} (Employee)
                </Typography>
              )}
            </Box>
            {isActive && (
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(customer);
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            {!isActive && isDragEnabled && (
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd(customer);
                }}
                title="Add to Active Schedule"
                sx={{ fontWeight: "bold", fontSize: "16px" }}
              >
                {">>"}
              </IconButton>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

const EmployeeScheduling = () => {
  const [customerList, setCustomerList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDay, setSelectedDay] = useState("");
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [activeCustomers, setActiveCustomers] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupTitle, setPopupTitle] = useState("");
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [searchCustomer, setSearchCustomer] = useState("");
  const [isNavigationBlockerVisible, setNavigationBlockerVisible] = useState(false);
  const [blockedNavigation, setBlockedNavigation] = useState(null);
  const [isCreateWorkOrderModalOpen, setCreateWorkOrderModalOpen] = useState(false);
  const [isDuplicateWarningVisible, setDuplicateWarningVisible] = useState(false);
  const [customerToAdd, setCustomerToAdd] = useState(null);
  const [customerInsertIndex, setCustomerInsertIndex] = useState(-1);
  const [existingEmployeeName, setExistingEmployeeName] = useState("");
  const [existingTaskToCancel, setExistingTaskToCancel] = useState(null);
  // Second-step confirmation shown when the user chooses "Permanent" on the
  // duplicate-assignment popup (the permanent move also auto-saves the schedule).
  const [isPermanentConfirmVisible, setPermanentConfirmVisible] = useState(false);
  // When set, an effect auto-saves the schedule once the permanent move has been
  // staged into pendingChanges (state updates are async, so we can't save inline).
  const [autoSaveAfterPermanent, setAutoSaveAfterPermanent] = useState(false);
  const [allWorkOrders, setAllWorkOrders] = useState([]);
  const [isSaveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false);
  const [isSaveTemplateConfirmOpen, setSaveTemplateConfirmOpen] = useState(false);
  const [isTemplateLibraryOpen, setTemplateLibraryOpen] = useState(false);
  const [hasReorderedSchedule, setHasReorderedSchedule] = useState(false);
  const [isSameDayConflictVisible, setSameDayConflictVisible] = useState(false);
  const [conflictDayLabel, setConflictDayLabel] = useState("");
  const hasPendingChangesRef = useRef(false);
  // Customers added to a past-day schedule whose first task is a future date.
  // Stored in a ref so they stay visible across saves without triggering re-renders.
  const futureAddedCustomersRef = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, userDetails } = useAuth();
  const canManageTemplates = ["Owner", "Manager", "Co-Owner", "Admin"].includes(
    userDetails?.role || userDetails?.designation
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  // Keep a ref in sync so the useBlocker condition never reads a stale closure
  useEffect(() => {
    hasPendingChangesRef.current = pendingChanges.length > 0 || hasReorderedSchedule;
  }, [pendingChanges, hasReorderedSchedule]);

  // Block navigation when there are unsaved changes (new customers OR reordered schedule)
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    hasPendingChangesRef.current &&
    currentLocation.pathname !== nextLocation.pathname
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [customers, employees, workOrders] = await Promise.all([
        fetchCustomers(companyId, "Y", null),
        fetchEmployees(companyId, null, null),
        fetchAllWorkOrders(companyId).catch(() => []),
      ]);
      setAllWorkOrders(Array.isArray(workOrders) ? workOrders : []);

      // Validate that we received data
      if (!Array.isArray(customers) || !Array.isArray(employees)) {
        throw new Error('Invalid data received from API');
      }

      const formattedCustomers = customers.map((customer) => ({
        id: customer.customerId,
        name: `${customer.custFirstName || ''} ${customer.custLastName || ''}`.trim() || 'Unknown Customer',
        firstName: (customer.custFirstName || '').toLowerCase(),
        lastName: (customer.custLastName || '').toLowerCase(),
        address: `${customer.custAddress || ''}, ${customer.custCity || ''}, ${customer.custZip || ''}`,
      }));

      const formattedEmployees = employees.map((emp) => ({
        id: emp.employeeId,
        name: `${emp.firstName} ${emp.lastName}`,
        phone: emp.contactNumber || '',
      }));

      setCustomerList(formattedCustomers);
      setEmployeeList(formattedEmployees);
    } catch (error) {
      console.error("Error loading data:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.status,
        data: error.response?.data
      });

      // Check if it's an authentication error (401)
      if (error.response?.status === 401) {
        // Authentication error - will be handled by axios interceptor
        // Don't show error popup, user will be redirected to login
        return;
      }

      // For other errors, show a more helpful message
      const errorMessage = error.response?.status === 500
        ? "Server error occurred. Please try again in a moment."
        : "Error loading data. Please check your connection and try again.";

      setPopupMessage(errorMessage);
      setPopupTitle("Error");
      setPopupVisible(true);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Returns the day-of-week index (0=Sun…6=Sat) from a work order's due/service date
  const getWorkOrderDayIndex = (wo) => {
    const dateStr = (wo.workOrder?.workOrderDueDate?.trim() || wo.workOrder?.initialServiceDate?.trim() || "");
    if (!dateStr) return -1;
    try {
      const date = new Date(dateStr.split("T")[0] + "T12:00:00");
      return date.getDay();
    } catch (e) {
      return -1;
    }
  };

  const calculateCustomerLists = useCallback(async () => {
    if (!selectedEmployee && selectedDay === "") return;

    // Create a set of active customer IDs for quick lookup
    const activeCustomerIds = new Set(customerList.map(c => c.id));

    // Shared cutoff used by both Layer 2 (task filter) and Layer 3 (work order filter)
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - STALE_TASK_DAYS);

    // If a day is selected, fetch tasks for that specific day
    let relevantTasks = [];
    let allDayTasks = []; // All tasks for this day across ALL employees (for Layer 3)
    if (selectedDay !== "") {
      try {
        const isoDate = getThisWeekDateForDay(selectedDay);

        console.log(`Fetching tasks for ${DAYS_OF_WEEK.find(d => d.value === selectedDay)?.label} (${isoDate})`);

        let fetchedTasks = await fetchTasksByDate(companyId, isoDate);

        // Temporary WO IDs (isTemporary=true) — created by the "this week only"
        // reassignment flow. Exclude them from the active schedule (Layer 1) so
        // they never appear as a permanent assignment. Regular on-demand WOs
        // (occurrence=0 but isTemporary=false) are unaffected.
        const temporaryWoIds = new Set(
          allWorkOrders
            .filter(wo => wo.workOrder?.isTemporary === true)
            .map(wo => wo.workOrder?.workOrderId)
            .filter(Boolean)
        );

        // A "route" on this screen = a weekly recurring Service work order (occurrence 7) —
        // exactly what the template snapshot captures. Restrict the active list to those so
        // the active schedule and a saved template always show the same customers. One-off /
        // daily (occurrence != 7), non-Service, and cancelled work orders are NOT weekly
        // routes and must not appear here (they were polluting the active list before).
        const weeklyServiceWoIds = new Set(
          allWorkOrders
            .filter(wo =>
              (wo.workOrder?.workOrderOccurance ?? 0) === 7 &&
              (wo.workOrder?.workOrderType ?? "Service") === "Service" &&
              wo.workOrder?.statusCode?.toLowerCase() !== "cancelled"
            )
            .map(wo => wo.workOrder?.workOrderId)
            .filter(Boolean)
        );
        // Guard the initial render where work orders haven't loaded yet — without this the
        // active list would briefly blank out (an empty set would exclude every task).
        const applyWeeklyFilter = allWorkOrders.length > 0;
        const isWeeklyRouteTask = (task) =>
          !applyWeeklyFilter || weeklyServiceWoIds.has(task.taskDetails?.wrkOdrId);

        // True when a task is a *live, displayable weekly-route task*: not cancelled, for an
        // active customer, not a stale "Not Started" leftover, not a temporary reassignment,
        // and tied to a current weekly Service work order. Only a task that passes this test
        // is allowed to "claim" a customer in the next-week dedupe below — otherwise a dead
        // leftover (e.g. a completed task whose work order was later cancelled) would wrongly
        // suppress the customer's real upcoming task and drop them off the schedule entirely.
        const isLiveWeeklyTask = (task) => {
          if (!task.taskDetails) return false;
          if (task.taskDetails.taskStatus === "Cancelled") return false;
          if (!activeCustomerIds.has(task.customer?.customerId)) return false;
          const status = task.taskDetails.taskStatus?.toLowerCase();
          if (status === "not started" || status === "not_started") {
            const dueDateStr = task.taskDetails.workOrderTaskDueDate || task.taskDate || "";
            if (dueDateStr) {
              const dueDate = new Date(dueDateStr.split("T")[0] + "T12:00:00");
              if (dueDate < twoWeeksAgo) return false;
            }
          }
          if (temporaryWoIds.has(task.taskDetails?.wrkOdrId)) return false;
          return isWeeklyRouteTask(task);
        };

        // When the selected day is in the past this week, the save path creates work orders
        // starting on the *next* occurrence (next week). Without also fetching next week's
        // tasks here, those newly saved customers disappear from the active list immediately
        // after saving and whenever the user navigates back to this view.
        const thisWeekDateObj = new Date(isoDate + "T12:00:00");
        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);
        if (thisWeekDateObj < todayMidnight) {
          try {
            const nextWeekDateObj = new Date(thisWeekDateObj);
            nextWeekDateObj.setDate(nextWeekDateObj.getDate() + 7);
            const nextWeekIso = toLocalDateString(nextWeekDateObj);
            const nextWeekTasks = await fetchTasksByDate(companyId, nextWeekIso);
            // Deduplicate by (employeeId, customerId) — not by customer alone.
            // Two different employees can legitimately share the same customer on
            // different weeks, so a company-wide customer-ID set would wrongly
            // suppress one employee's next-week tasks when another employee already
            // has that customer this week.
            //
            // CRITICAL: only a *live weekly-route* this-week task may cover a customer.
            // A dead leftover this week (e.g. a completed task on a now-cancelled work
            // order) must NOT suppress the customer's real upcoming task next week — that
            // is the bug that silently dropped customers (e.g. Axar) from the schedule.
            const thisWeekEmpCustPairs = new Set(
              fetchedTasks
                .filter(isLiveWeeklyTask)
                .filter((t) => t.employee?.employeeId != null && t.customer?.customerId != null)
                .map((t) => `${t.employee.employeeId}|${t.customer.customerId}`)
            );
            fetchedTasks = [
              ...fetchedTasks,
              ...nextWeekTasks.filter((t) => {
                if (!t.employee?.employeeId || !t.customer?.customerId) return true;
                return !thisWeekEmpCustPairs.has(`${t.employee.employeeId}|${t.customer.customerId}`);
              }),
            ];
          } catch (err) {
            console.warn("Could not fetch next-week tasks for past day:", err);
          }
        }

        // All valid tasks for this day (no cancelled, no inactive) — used for Layer 2 & 3.
        // Also exclude stale "Not Started" tasks whose due date is older than 14 days —
        // orphaned tasks left over from data cleanup can block customers indefinitely otherwise.
        allDayTasks = fetchedTasks.filter((task) => {
          if (!task.taskDetails) return false;
          if (task.taskDetails.taskStatus === "Cancelled") return false;
          if (!activeCustomerIds.has(task.customer?.customerId)) return false;
          const status = task.taskDetails.taskStatus?.toLowerCase();
          if (status === "not started" || status === "not_started") {
            const dueDateStr = task.taskDetails.workOrderTaskDueDate || task.taskDate || "";
            if (dueDateStr) {
              const dueDate = new Date(dueDateStr.split("T")[0] + "T12:00:00");
              if (dueDate < twoWeeksAgo) return false;
            }
          }
          return true;
        });

        // Tasks for THIS employee on this day — used for active list (Layer 1)
        relevantTasks = (selectedEmployee
          ? allDayTasks.filter(task => task.employee?.employeeId === selectedEmployee.id)
          : allDayTasks
        ).filter(task =>
          !temporaryWoIds.has(task.taskDetails?.wrkOdrId) && isWeeklyRouteTask(task)
        );

        console.log("Relevant Tasks Found:", relevantTasks.length);
      } catch (error) {
        console.error("Error fetching tasks:", error);
        relevantTasks = [];
      }
    }

    // Create a map of customer ID to task details
    const customerTaskMap = {};
    relevantTasks.forEach((task) => {
      if (task.customer) {
        customerTaskMap[task.customer.customerId] = task;
      }
    });

    // Layer 1: customer IDs for THIS employee on THIS day (shown as active).
    //
    // SINGLE SOURCE OF TRUTH: a customer is on this employee's day-route if and only if
    // they have an active (non-cancelled) weekly (occurrence-7) Service work order for this
    // employee on this day — exactly how the template snapshot and the restore engine define
    // a route. Membership is taken from WORK ORDERS, not from which week the next task lands
    // in. Keying off tasks (this/next week) is what made customers disappear here while still
    // living in the template: a pushed-out "gap" work order (next visit >2 weeks out) or a
    // stale one would vanish from this list but not from the template/restore. One definition
    // everywhere = the three views always agree.
    const scheduledWorkOrders = (selectedEmployee && selectedDay !== "")
      ? allWorkOrders.filter((wo) => {
          const w = wo.workOrder;
          if (!w) return false;
          if (w.employeeId !== selectedEmployee.id) return false;
          if ((w.workOrderOccurance ?? 0) !== 7) return false;
          if ((w.workOrderType ?? "Service") !== "Service") return false;
          if (w.statusCode?.toLowerCase() === "cancelled") return false;
          if (getWorkOrderDayIndex(wo) !== parseInt(selectedDay)) return false;
          return activeCustomerIds.has(w.customerId);
        })
      : [];
    // Fall back to the task-derived list only during the brief initial render before work
    // orders have loaded (an empty allWorkOrders would otherwise blank the list).
    const scheduledCustomerIds = allWorkOrders.length > 0
      ? [...new Set(scheduledWorkOrders.map((wo) => wo.workOrder.customerId).filter(Boolean))]
      : relevantTasks.map((task) => task.customer?.customerId).filter(Boolean);

    console.log("Scheduled Customer IDs:", scheduledCustomerIds);

    // Layer 3: customers with OTHER employees on this same day
    const otherEmployeeSameDayIds = new Set(
      selectedEmployee
        ? allDayTasks
            .filter(task => task.employee?.employeeId !== selectedEmployee.id)
            .map(task => task.customer?.customerId)
            .filter(Boolean)
        : []
    );

    // Layer 2: customers with THIS employee on OTHER days of the week.
    // Only consider weekly recurring (occurrence=7) work orders — one-time/infrequent
    // services (e.g. filter breakdown with occurrence=90) should never block regular
    // scheduling. Also ignore stale work orders whose due date is older than 14 days,
    // which can linger with "Not Started" status after data cleanup.
    const thisEmployeeOtherDaysIds = new Set(
      selectedEmployee
        ? allWorkOrders
            .filter(wo => {
              if (!wo.workOrder) return false;
              const woDay = getWorkOrderDayIndex(wo);
              if (
                wo.workOrder.employeeId !== selectedEmployee.id ||
                woDay === parseInt(selectedDay) ||
                wo.workOrder.statusCode?.toLowerCase() === "cancelled"
              ) return false;
              // Only weekly recurring services should block other-day scheduling
              if ((wo.workOrder.workOrderOccurance ?? 0) !== 7) return false;
              // Skip stale work orders whose due date has passed by more than 14 days
              const dateStr = (wo.workOrder.workOrderDueDate?.trim() || wo.workOrder.initialServiceDate?.trim() || "");
              if (!dateStr) return false;
              const woDate = new Date(dateStr.split("T")[0] + "T12:00:00");
              return woDate >= twoWeeksAgo;
            })
            .map(wo => wo.workOrder.customerId)
            .filter(Boolean)
        : []
    );

    // All restricted IDs: can't appear in Available
    const allRestrictedIds = new Set([
      ...scheduledCustomerIds,
      ...otherEmployeeSameDayIds,
      ...thisEmployeeOtherDaysIds,
    ]);

    // Split customers into active and available
    // Enrich active customers with day and employee info
    const active = customerList
      .filter((customer) => scheduledCustomerIds.includes(customer.id))
      .map((customer) => {
        const task = customerTaskMap[customer.id];
        const enrichedCustomer = { ...customer };

        // Store the full task data for later use (e.g., for deletion)
        enrichedCustomer.taskData = task;

        // Add day label if available
        if (selectedDay !== "") {
          const dayInfo = DAYS_OF_WEEK.find((d) => d.value === selectedDay);
          enrichedCustomer.dayLabel = dayInfo?.label || "";
        }

        // Add employee name if available
        if (task && task.employee?.employeeId) {
          const employee = employeeList.find(
            (emp) => emp.id === task.employee.employeeId,
          );
          enrichedCustomer.employeeName = employee?.name || "";
        }

        return enrichedCustomer;
      });

    // Restore the saved route order; tasks without a routeOrder go to the end
    active.sort((a, b) => {
      const orderA = a.taskData?.taskDetails?.routeOrder ?? Infinity;
      const orderB = b.taskData?.taskDetails?.routeOrder ?? Infinity;
      return orderA - orderB;
    });

    // Also include pending changes in active list (customers added but not saved yet)
    const pendingCustomerIds = pendingChanges.map((c) => c.id);
    const pendingInActive = pendingChanges.filter(
      (customer) => !scheduledCustomerIds.includes(customer.id)
    );

    // Include customers saved to a future-occurrence date (e.g., added to a past
    // day's schedule — their first task is next week, so they don't appear in
    // this week's task fetch, but we still want them visible in the active list).
    const handledIds = new Set([...scheduledCustomerIds, ...pendingCustomerIds]);
    const futureAddedActive = futureAddedCustomersRef.current.filter(
      (c) => !handledIds.has(c.id)
    );

    // Only hide customers already active on this day with this employee (Layer 1).
    // Layer 2 (same employee, other day) and Layer 3 (other employee, same day)
    // are still shown here; a conflict popup fires in handleAddCustomer instead.
    const available = customerList.filter(
      (customer) =>
        !scheduledCustomerIds.includes(customer.id) &&
        !pendingCustomerIds.includes(customer.id) &&
        !futureAddedCustomersRef.current.some((c) => c.id === customer.id),
    );

    // Combine scheduled customers with pending and future-added
    setActiveCustomers([...active, ...pendingInActive, ...futureAddedActive]);
    setAvailableCustomers(available);
    setTasks(relevantTasks);
  }, [selectedEmployee, selectedDay, customerList, employeeList, pendingChanges, companyId, allWorkOrders]);

  // Handle browser navigation (refresh, back button, close tab)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (pendingChanges.length > 0 || hasReorderedSchedule) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [pendingChanges, hasReorderedSchedule]);

  // Show confirmation modal when navigation is blocked
  useEffect(() => {
    if (blocker.state === "blocked") {
      setBlockedNavigation(blocker);
      setNavigationBlockerVisible(true);
    }
  }, [blocker.state]);

  // Load initial data
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Periodic refresh to prevent stale data and token expiration
  useEffect(() => {
    if (!companyId) return;

    // Refresh data every 5 minutes, but skip when the user has unsaved changes.
    // Calling loadData() resets customerList → recalculates activeCustomers from DB,
    // which would wipe any unsaved drag reordering or pending additions.
    const refreshInterval = setInterval(() => {
      if (pendingChanges.length === 0 && !hasReorderedSchedule) {
        console.log('Refreshing employee scheduling data...');
        loadData();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, [companyId, loadData, pendingChanges, hasReorderedSchedule]);

  // Clear future-added customers when the employee or day selection changes
  useEffect(() => {
    futureAddedCustomersRef.current = [];
  }, [selectedEmployee, selectedDay]);

  // Update lists when filters change
  useEffect(() => {
    if (selectedEmployee || selectedDay !== "") {
      calculateCustomerLists();
    } else {
      setAvailableCustomers(customerList);
      setActiveCustomers([]);
      setPendingChanges([]);
      setHasReorderedSchedule(false);
      hasPendingChangesRef.current = false;
      setTasks([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, selectedDay, customerList]);

  // Auto-select employee from navigation state (e.g., from View Employees page)
  useEffect(() => {
    if (location.state?.employeeId && employeeList.length > 0 && !selectedEmployee) {
      const employeeToSelect = employeeList.find(
        (emp) => emp.id === location.state.employeeId
      );
      if (employeeToSelect) {
        setSelectedEmployee(employeeToSelect);
      }
    }
  }, [location.state, employeeList, selectedEmployee]);

  // Returns "yyyy-MM-dd" from local date components — no UTC conversion,
  // so the date string always matches what the user sees on their calendar.
  const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // Used when CREATING work orders — picks the next occurrence of the chosen
  // weekday. Returns "yyyy-MM-ddT12:00:00Z" (noon UTC) matching iOS and Android.
  //
  // Same-day case is gated by `forceThisWeek`:
  //   • forceThisWeek = true  → schedule for *today*    (user chose "This Week")
  //   • forceThisWeek = false → schedule for *today + 7* (user chose "Next Week"
  //                              or the 6 PM cutoff has passed)
  // Mirrors iOS getNextOccurrence(forceThisWeek:) and Android
  // EmployeeSchedulingViewModel.getNextOccurrence(forceThisWeek).
  const getNextDateForDay = (dayOfWeek, forceThisWeek = false) => {
    const today = new Date();
    const currentDay = today.getDay();
    let daysUntilTarget = (dayOfWeek - currentDay + 7) % 7;
    if (daysUntilTarget === 0 && !forceThisWeek) {
      daysUntilTarget = 7;
    }
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilTarget);
    return `${toLocalDateString(nextDate)}T12:00:00Z`;
  };

  // Used when VIEWING the schedule — returns this week's "yyyy-MM-dd" for the day,
  // even if the day has already passed (e.g. Monday when today is Wednesday).
  const getThisWeekDateForDay = (dayOfWeek) => {
    const today = new Date();
    const currentDay = today.getDay();
    const daysOffset = dayOfWeek - currentDay;
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysOffset);
    return toLocalDateString(targetDate);
  };

  const handleDragStart = (event) => {
    console.log("=== DRAG START ===");
    console.log("Dragging item ID:", event.active.id);
    console.log("Can Drag Drop:", canDragDrop);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    console.log("=== DRAG END ===");
    console.log("Active ID:", active?.id);
    console.log("Over ID:", over?.id);
    console.log("Can Drag Drop:", canDragDrop);

    if (!over) {
      console.log("No drop target");
      return;
    }

    // Check if drag and drop is allowed
    if (!canDragDrop) {
      console.log("Drag and drop not allowed - need both employee and day");
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Find which list the dragged item is currently in
    const isFromActive = activeCustomers.some((c) => c.id === activeId);
    const isFromAvailable = availableCustomers.some((c) => c.id === activeId);

    console.log("Is From Active:", isFromActive);
    console.log("Is From Available:", isFromAvailable);

    // Find which list we're dropping into
    // Check if overId is a container ID or a customer ID
    let isMovingToActive = overId === "active-customers";
    let isMovingToAvailable = overId === "available-customers";

    // If not a container, check if it's a customer in one of the lists
    if (!isMovingToActive && !isMovingToAvailable) {
      isMovingToActive = activeCustomers.some((c) => c.id === overId);
      isMovingToAvailable = availableCustomers.some((c) => c.id === overId);
    }

    console.log("Is Moving To Active:", isMovingToActive);
    console.log("Is Moving To Available:", isMovingToAvailable);

    // If we're dropping in the same list, reorder if it's the active list
    if (isFromActive && isMovingToActive) {
      const oldIndex = activeCustomers.findIndex((c) => c.id === activeId);
      const newIndex = activeCustomers.findIndex((c) => c.id === overId);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setActiveCustomers(arrayMove(activeCustomers, oldIndex, newIndex));
        setHasReorderedSchedule(true);
        hasPendingChangesRef.current = true; // sync update so useBlocker sees it immediately
      }
      return;
    }
    if (isFromAvailable && isMovingToAvailable) {
      console.log("Dropped in same list - no action");
      return;
    }

    const customer = customerList.find((c) => c.id === activeId);
    if (!customer) {
      console.log("Customer not found");
      return;
    }

    if (isMovingToActive && isFromAvailable) {
      console.log("Moving customer to ACTIVE:", customer.name);
      const dropIndex = activeCustomers.findIndex((c) => c.id === overId);
      let insertAt = -1; // default: append
      if (dropIndex !== -1) {
        // Determine whether to insert before or after the hovered element by comparing
        // the dragged item's vertical center against the over element's vertical center.
        const overRect = over.rect;
        const activeTranslated = active.rect.current.translated;
        if (overRect && activeTranslated) {
          const draggedMidY = activeTranslated.top + activeTranslated.height / 2;
          const overMidY = overRect.top + overRect.height / 2;
          insertAt = draggedMidY > overMidY ? dropIndex + 1 : dropIndex;
        } else {
          insertAt = dropIndex;
        }
      }
      handleAddCustomer(customer, insertAt);
    } else if (isMovingToAvailable && isFromActive) {
      console.log("Moving customer to AVAILABLE:", customer.name);
      // Moving from active to available
      setAvailableCustomers([...availableCustomers, customer]);
      setActiveCustomers(activeCustomers.filter((c) => c.id !== customer.id));

      // Remove from pending changes
      setPendingChanges(pendingChanges.filter((c) => c.id !== customer.id));
    } else {
      console.log("No valid move detected");
    }
  };

  const handleRemoveCustomer = (customer) => {
    setCustomerToDelete(customer);
    setDeletePopupVisible(true);
  };

  const handleAddCustomer = async (customer, insertIndex = -1) => {
    // Check if customer is already scheduled with a different employee on this day.
    // We check both this week's and next week's occurrence because work orders for
    // past-day schedules are created on the next occurrence (next week).
    if (selectedDay !== "" && companyId) {
      try {
        const isoDate = getThisWeekDateForDay(selectedDay);
        const thisWeekTasks = await fetchTasksByDate(companyId, isoDate);

        const nextWeekDate = new Date(isoDate + "T12:00:00");
        nextWeekDate.setDate(nextWeekDate.getDate() + 7);
        const nextWeekIso = toLocalDateString(nextWeekDate);
        let nextWeekTasks = [];
        try {
          nextWeekTasks = await fetchTasksByDate(companyId, nextWeekIso);
        } catch (_) {
          // next-week fetch is best-effort
        }

        const allTasksForDay = [...thisWeekTasks, ...nextWeekTasks];

        // Find if this customer has a task with a different employee
        const existingTask = allTasksForDay.find(
          (task) =>
            task.customer?.customerId === customer.id &&
            task.taskDetails?.taskStatus?.toLowerCase() !== "cancelled" &&
            task.taskDetails?.taskStatus?.toLowerCase() !== "canceled" &&
            task.employee?.employeeId !== selectedEmployee?.id
        );

        if (existingTask) {
          const otherEmployee = employeeList.find(
            (emp) => emp.id === existingTask.employee?.employeeId
          );

          setCustomerToAdd(customer);
          setCustomerInsertIndex(insertIndex);
          setExistingEmployeeName(otherEmployee?.name || "Another employee");
          setExistingTaskToCancel(existingTask);
          setDuplicateWarningVisible(true);
          return;
        }

        // Layer 2: same employee already has this customer on a different day
        if (selectedEmployee) {
          const twoWeeksAgo = new Date();
          twoWeeksAgo.setDate(twoWeeksAgo.getDate() - STALE_TASK_DAYS);
          const conflictWO = allWorkOrders.find((wo) => {
            if (!wo.workOrder) return false;
            if (wo.workOrder.customerId !== customer.id) return false;
            if (wo.workOrder.employeeId !== selectedEmployee.id) return false;
            const woDay = getWorkOrderDayIndex(wo);
            if (woDay === parseInt(selectedDay)) return false;
            if (wo.workOrder.statusCode?.toLowerCase() === "cancelled") return false;
            if ((wo.workOrder.workOrderOccurance ?? 0) !== 7) return false;
            const dateStr =
              wo.workOrder.workOrderDueDate?.trim() ||
              wo.workOrder.initialServiceDate?.trim() ||
              "";
            if (!dateStr) return false;
            const woDate = new Date(dateStr.split("T")[0] + "T12:00:00");
            return woDate >= twoWeeksAgo;
          });
          if (conflictWO) {
            const conflictDay = DAYS_OF_WEEK.find(
              (d) => d.value === getWorkOrderDayIndex(conflictWO)
            );
            setCustomerToAdd(customer);
            setCustomerInsertIndex(insertIndex);
            setConflictDayLabel(conflictDay?.label || "another day");
            setSameDayConflictVisible(true);
            return;
          }
        }
      } catch (error) {
        console.error("Error checking for duplicate assignment:", error);
      }
    }

    confirmAddCustomer(customer, insertIndex);
  };

  const confirmAddCustomer = (customer, insertIndex = -1) => {
    let newActiveCustomers;
    if (insertIndex >= 0 && insertIndex < activeCustomers.length) {
      newActiveCustomers = [...activeCustomers];
      newActiveCustomers.splice(insertIndex, 0, customer);
    } else {
      newActiveCustomers = [...activeCustomers, customer];
    }
    setActiveCustomers(newActiveCustomers);
    setAvailableCustomers(availableCustomers.filter((c) => c.id !== customer.id));

    // Track as pending change
    if (!pendingChanges.find((c) => c.id === customer.id)) {
      setPendingChanges([...pendingChanges, customer]);
      hasPendingChangesRef.current = true; // sync update so useBlocker sees it immediately
    }
  };

  // "Permanent" button on the duplicate-assignment popup: don't act yet — first show a
  // confirmation warning, since this both removes the customer from the other employee
  // and saves the schedule immediately.
  const onRequestPermanentConfirm = () => {
    setDuplicateWarningVisible(false);
    setPermanentConfirmVisible(true);
  };

  // User backed out of the permanent confirmation — return to the original choice popup.
  const onCancelPermanentConfirm = () => {
    setPermanentConfirmVisible(false);
    setDuplicateWarningVisible(true);
  };

  const onConfirmDuplicateAssignment = async () => {
    // Cancel the existing work order for the other employee (prevents pool from coming back)
    if (existingTaskToCancel) {
      const otherEmployeeId = existingTaskToCancel.employee?.employeeId;
      const otherCustomerId = existingTaskToCancel.customer?.customerId;
      let workOrderToCancel = allWorkOrders.find(
        (wo) =>
          wo.workOrder?.customerId === otherCustomerId &&
          wo.workOrder?.employeeId === otherEmployeeId &&
          wo.workOrder?.statusCode?.toLowerCase() !== "cancelled"
      );
      if (!workOrderToCancel) {
        workOrderToCancel = allWorkOrders.find(
          (wo) =>
            wo.workOrder?.customerId === otherCustomerId &&
            wo.workOrder?.statusCode?.toLowerCase() !== "cancelled" &&
            (wo.workOrder?.workOrderOccurance ?? 7) === 7
        );
      }
      if (workOrderToCancel) {
        try {
          await updateWorkOrder({
            ...workOrderToCancel.workOrder,
            statusCode: "Cancelled",
          });
          setAllWorkOrders(
            allWorkOrders.map((wo) =>
              wo.workOrder?.workOrderId === workOrderToCancel.workOrder.workOrderId
                ? { ...wo, workOrder: { ...wo.workOrder, statusCode: "Cancelled" } }
                : wo
            )
          );
        } catch (err) {
          console.warn("Could not cancel other employee's work order:", err);
        }
      }

      // Cancel the existing task with the other employee
      if (existingTaskToCancel.taskDetails?.taskId) {
        try {
          await updateTask({
            ...existingTaskToCancel.taskDetails,
            taskStatus: "Cancelled",
            evidences: existingTaskToCancel.taskDetails.evidences || [],
          });
        } catch (err) {
          console.warn("Could not cancel previous employee's task:", err);
        }
      }
    }

    if (customerToAdd) {
      confirmAddCustomer(customerToAdd, customerInsertIndex);
    }
    // Permanent move is confirmed — stage it and let the effect below auto-save.
    setAutoSaveAfterPermanent(true);
    setPermanentConfirmVisible(false);
    setDuplicateWarningVisible(false);
    setCustomerToAdd(null);
    setCustomerInsertIndex(-1);
    setExistingEmployeeName("");
    setExistingTaskToCancel(null);
  };

  // Auto-save once the permanent move has landed in pendingChanges. State updates are
  // batched/async, so confirmAddCustomer's pendingChanges isn't visible synchronously —
  // this effect runs on the next render when it is, then triggers the normal save path.
  useEffect(() => {
    if (autoSaveAfterPermanent && pendingChanges.length > 0) {
      setAutoSaveAfterPermanent(false);
      const isToday = parseInt(selectedDay) === new Date().getDay();
      handleSaveSchedule(isToday);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveAfterPermanent, pendingChanges]);

  const onCancelDuplicateAssignment = () => {
    setDuplicateWarningVisible(false);
    setCustomerToAdd(null);
    setExistingEmployeeName("");
    setExistingTaskToCancel(null);
  };

  // "This week only":
  //   1. Cancel Employee A's existing task so they don't show the customer that week.
  //   2. Cancel Employee A's occurrence=7 WO, then recreate it starting the week AFTER
  //      the temporary date — this stops the Sunday job from regenerating the task
  //      for Employee A on the same week.
  //   3. Create Employee B's occurrence=0 WO+task for the one-off week.
  //   Nothing is added to activeCustomers or pendingChanges — the route editor is untouched.
  const onTemporaryAssign = async () => {
    const customer = customerToAdd;
    const taskToCancel = existingTaskToCancel; // Employee A's task

    // Close popup before async work
    setDuplicateWarningVisible(false);
    setCustomerToAdd(null);
    setCustomerInsertIndex(-1);
    setExistingEmployeeName("");
    setExistingTaskToCancel(null);

    if (!customer || !selectedEmployee || selectedDay === "") return;

    const dayLabel = DAYS_OF_WEEK.find((d) => d.value === selectedDay)?.label || "";
    setSaving(true);
    try {
      // "This week only" must target the CURRENT week's occurrence: if the selected day is
      // today, schedule it for today (not next week); if it already passed this week, it
      // rolls to next week naturally. Force-this-week only when today IS the selected day —
      // matching the regular save path and the backend's nextOccurrenceTimestamp.
      const isToday = parseInt(selectedDay) === new Date().getDay();
      const nextDateStr = getNextDateForDay(selectedDay, isToday);
      const targetDate = nextDateStr.split("T")[0];

      // PRM_0043: Guard against duplicate temp WO if the user presses "This week only" more than once.
      const existingTemp = allWorkOrders.find(
        (wo) =>
          wo.workOrder?.isTemporary === true &&
          wo.workOrder?.customerId === customer.id &&
          wo.workOrder?.employeeId === selectedEmployee.id &&
          wo.workOrder?.workOrderDueDate?.startsWith(targetDate)
      );
      if (existingTemp) {
        setPopupTitle("Already Scheduled This Week");
        setPopupMessage(
          `${customer.name} is already assigned to ${selectedEmployee.name}'s route for this ${dayLabel}. Check the Routes Summary to view it.`
        );
        setPopupVisible(true);
        return;
      }

      let poolId = null;
      try {
        const poolData = await fetchPoolByCustomer(companyId, customer.id);
        poolId = poolData?.poolId || null;
      } catch (_) {}

      // Step 1 — cancel Employee A's task so they won't show this customer that week.
      // Cancel the known task first, then also cancel any pre-generated task Employee A
      // has on the target date (nextDateStr). The conflict check uses both this-week and
      // next-week tasks, so existingTaskToCancel might be a past occurrence while a
      // separate pre-generated task for the upcoming date still exists.
      const otherEmpIdStep1 = taskToCancel?.employee?.employeeId;
      if (taskToCancel?.taskDetails?.taskId) {
        try {
          await updateTask({
            ...taskToCancel.taskDetails,
            taskStatus: "Cancelled",
            evidences: taskToCancel.taskDetails.evidences || [],
          });
        } catch (_) {}
      }
      if (otherEmpIdStep1) {
        try {
          const targetDate = nextDateStr.split("T")[0];
          const tasksOnTarget = await fetchTasksByDate(companyId, targetDate);
          for (const t of tasksOnTarget) {
            if (
              t.customer?.customerId === customer.id &&
              t.employee?.employeeId === otherEmpIdStep1 &&
              t.taskDetails?.taskId !== taskToCancel?.taskDetails?.taskId &&
              t.taskDetails?.taskStatus?.toLowerCase() !== "cancelled" &&
              t.taskDetails?.taskStatus?.toLowerCase() !== "canceled"
            ) {
              try {
                await updateTask({
                  ...t.taskDetails,
                  taskStatus: "Cancelled",
                  evidences: t.taskDetails.evidences || [],
                });
              } catch (_) {}
            }
          }
        } catch (_) {}
      }

      // Step 2 — Employee A's recurring work order is intentionally LEFT UNCHANGED.
      // Only their task for this specific occurrence is cancelled (Step 1 above).
      // Their regular schedule automatically resumes next week without any WO changes.

      // Step 3 — create Employee B's one-time WO+task
      await postWorkOrder({
        poolId,
        customerId: customer.id,
        employeeId: selectedEmployee.id,
        workOrderType: "Service",
        workOrderOccurance: 0,
        workOrderServiceType: "Regular Service",
        workOrderComment: `Temporary assignment for ${dayLabel} — this week only`,
        workOrderDueDate: nextDateStr,
        initialServiceDate: nextDateStr,
        lastServiceDate: null,
        statusCode: "Not Started",
        workOrderPart: null,
        workOrderCost: null,
        isTemporary: true,
      });

      // Refresh work orders so conflict detection reflects the new state
      try {
        const refreshed = await fetchAllWorkOrders(companyId);
        setAllWorkOrders(Array.isArray(refreshed) ? refreshed : allWorkOrders);
      } catch (_) {}

      setPopupTitle("Scheduled (This Week Only)");
      setPopupMessage(
        `${customer.name} has been added to ${selectedEmployee.name}'s ${dayLabel} route for this week only. ` +
        `Check the Routes Summary to view the assignment. They will automatically return to their regular schedule the following week.`
      );
      setPopupVisible(true);
    } catch (err) {
      console.error("Error creating temporary assignment:", err);
      setPopupMessage("Could not create the temporary assignment. Please try again.");
      setPopupTitle("Error");
      setPopupVisible(true);
    } finally {
      setSaving(false);
    }
  };

  const onConfirmSameDayConflict = () => {
    if (customerToAdd) {
      confirmAddCustomer(customerToAdd, customerInsertIndex);
    }
    setSameDayConflictVisible(false);
    setCustomerToAdd(null);
    setCustomerInsertIndex(-1);
    setConflictDayLabel("");
  };

  const onCancelSameDayConflict = () => {
    setSameDayConflictVisible(false);
    setCustomerToAdd(null);
    setCustomerInsertIndex(-1);
    setConflictDayLabel("");
  };

  const onConfirmDelete = async () => {
    if (!customerToDelete) return;

    // Check if this is a pending change (not yet saved) or an existing task
    const isPendingChange = pendingChanges.some((c) => c.id === customerToDelete.id);

    try {
      if (!isPendingChange) {
        // NOTE: do NOT gate this on taskData. The active list is built from work orders, so a
        // member whose next visit is outside the displayed weeks (or stale) has no task in
        // view (taskData undefined) but is still a real saved route that must be cancellable.
        // We cancel the work order via allWorkOrders (keyed by customer), and cancel the task
        // instance only if one happens to exist.
        const task = customerToDelete.taskData;

        // Cancel the work order so future recurring tasks are not generated (prevents pools coming back)
        let workOrderToCancel = allWorkOrders.find(
          (wo) =>
            wo.workOrder?.customerId === customerToDelete.id &&
            wo.workOrder?.employeeId === selectedEmployee?.id &&
            wo.workOrder?.statusCode?.toLowerCase() !== "cancelled"
        );
        // Fallback: match by customerId only (handles cases where employee differs or allWorkOrders was stale)
        if (!workOrderToCancel) {
          workOrderToCancel = allWorkOrders.find(
            (wo) =>
              wo.workOrder?.customerId === customerToDelete.id &&
              wo.workOrder?.statusCode?.toLowerCase() !== "cancelled" &&
              (wo.workOrder?.workOrderOccurance ?? 7) === 7
          );
        }
        if (workOrderToCancel) {
          try {
            await updateWorkOrder({
              ...workOrderToCancel.workOrder,
              statusCode: "Cancelled",
            });
            setAllWorkOrders(
              allWorkOrders.map((wo) =>
                wo.workOrder?.workOrderId === workOrderToCancel.workOrder.workOrderId
                  ? { ...wo, workOrder: { ...wo.workOrder, statusCode: "Cancelled" } }
                  : wo
              )
            );
          } catch (err) {
            console.warn("Could not cancel work order:", err);
          }
        }

        // Also cancel the current task instance (only if one exists in the current view)
        if (task?.taskDetails?.taskId) {
          try {
            await updateTask({
              ...task.taskDetails,
              taskId: task.taskDetails.taskId,
              taskStatus: "Cancelled",
              evidences: [],
            });
          } catch (err) {
            console.warn("Could not cancel task:", err);
          }
        }

        // Update UI directly to preserve the user's route order (don't call calculateCustomerLists)
        setActiveCustomers(activeCustomers.filter((c) => c.id !== customerToDelete.id));
        setAvailableCustomers([...availableCustomers, customerToDelete]);
        setTasks(tasks.filter((t) => t.customer?.customerId !== customerToDelete.id));
        // Remove from future-added ref so they don't reappear on the next periodic refresh
        futureAddedCustomersRef.current = futureAddedCustomersRef.current.filter(
          (c) => c.id !== customerToDelete.id
        );
      } else {
        // For pending changes, just update UI state without reloading
        setAvailableCustomers([...availableCustomers, customerToDelete]);
        setActiveCustomers(
          activeCustomers.filter((c) => c.id !== customerToDelete.id),
        );
        setPendingChanges(
          pendingChanges.filter((c) => c.id !== customerToDelete.id),
        );
      }
    } catch (error) {
      console.error("Error cancelling task:", error);
      setPopupMessage("Error cancelling task. Please try again.");
      setPopupTitle("Error");
      setPopupVisible(true);
    }

    setDeletePopupVisible(false);
    setCustomerToDelete(null);
  };

  const onCancelDelete = () => {
    setDeletePopupVisible(false);
    setCustomerToDelete(null);
  };

  const handleNavigationBlockerSave = async () => {
    // Save the schedule first
    await handleSaveSchedule();
    // After successful save, pendingChanges will be empty, so we can proceed
    if (blockedNavigation) {
      blockedNavigation.proceed();
      setNavigationBlockerVisible(false);
      setBlockedNavigation(null);
    }
  };

  const handleNavigationBlockerCancel = () => {
    if (blockedNavigation) {
      blockedNavigation.reset();
      setNavigationBlockerVisible(false);
      setBlockedNavigation(null);
    }
  };

  const handleNavigationBlockerDiscard = () => {
    // Disarm the blocker FIRST so proceed() doesn't get caught again
    hasPendingChangesRef.current = false;
    setPendingChanges([]);
    setHasReorderedSchedule(false);
    setNavigationBlockerVisible(false);
    if (blockedNavigation) {
      const nav = blockedNavigation;
      setBlockedNavigation(null);
      nav.proceed(); // continue to wherever the user originally wanted to go
    }
  };

  const handleSaveButtonClick = () => {
    if ((pendingChanges.length === 0 && !hasReorderedSchedule) || !selectedEmployee || selectedDay === "")
      return;

    const now = new Date();
    const isToday = parseInt(selectedDay) === now.getDay();
    handleSaveSchedule(isToday);
  };

  const handleSaveSchedule = async (forceThisWeek = false) => {
    if ((pendingChanges.length === 0 && !hasReorderedSchedule) || !selectedEmployee || selectedDay === "")
      return;

    try {
      setSaving(true);

      const dayName =
        DAYS_OF_WEEK.find((d) => d.value === selectedDay)?.label || "";
      const nextDateStr = getNextDateForDay(selectedDay, forceThisWeek); // "yyyy-MM-ddT12:00:00Z"

      // Capture the desired display order before any async work changes state
      const orderedCustomerIds = activeCustomers.map((c) => c.id);

      // Create work orders for all newly added customers
      if (pendingChanges.length > 0) {
        const workOrderPromises = pendingChanges.map(async (customer) => {
          try {
            let poolId = null;
            try {
              const poolData = await fetchPoolByCustomer(companyId, customer.id);
              poolId = poolData?.poolId || null;
            } catch (err) {
              console.warn(`No pool found for customer ${customer.id}`);
            }

            // Cancel any existing active work order for this customer+employee
            // before creating a new one to prevent duplicate work orders
            const existingWO = allWorkOrders.find(
              (wo) =>
                wo.workOrder?.customerId === customer.id &&
                wo.workOrder?.employeeId === selectedEmployee.id &&
                wo.workOrder?.statusCode?.toLowerCase() !== "cancelled"
            );
            if (existingWO) {
              try {
                await updateWorkOrder({
                  ...existingWO.workOrder,
                  statusCode: "Cancelled",
                });
              } catch (err) {
                console.warn(
                  `Could not cancel existing work order for customer ${customer.id}:`,
                  err
                );
              }
            }

            const workOrderData = {
              poolId: poolId,
              customerId: customer.id,
              employeeId: selectedEmployee.id,
              workOrderType: "Service",
              workOrderOccurance: 7,
              workOrderServiceType: "Regular Service",
              workOrderComment: `Scheduled for ${dayName}s`,
              workOrderDueDate: nextDateStr,
              initialServiceDate: nextDateStr,
              lastServiceDate: null,
              statusCode: "Not Started",
              workOrderPart: null,
              workOrderCost: null,
            };

            return await postWorkOrder(workOrderData);
          } catch (error) {
            console.error(
              `Error creating work order for customer ${customer.id}:`,
              error,
            );
            throw error;
          }
        });

        await Promise.all(workOrderPromises);

        // Refresh allWorkOrders so newly created work orders are findable during delete
        const refreshedWOs = await fetchAllWorkOrders(companyId).catch(() => []);
        setAllWorkOrders(Array.isArray(refreshedWOs) ? refreshedWOs : allWorkOrders);

        // If the work orders were created for a future date (past-day schedule),
        // track those customers so they remain visible after pendingChanges clears.
        const thisWeekIso = getThisWeekDateForDay(selectedDay);
        const nextOccIso = nextDateStr.split("T")[0];
        if (nextOccIso !== thisWeekIso) {
          const alreadyTracked = new Set(futureAddedCustomersRef.current.map((c) => c.id));
          pendingChanges.forEach((c) => {
            if (!alreadyTracked.has(c.id)) {
              futureAddedCustomersRef.current = [...futureAddedCustomersRef.current, c];
            }
          });
        }
      }

      // Fetch fresh tasks for routeOrder writes.
      // For past-day schedules, also fetch next-occurrence tasks so newly added
      // customers (whose first task is next week) get their routeOrder saved too.
      const isoDate = getThisWeekDateForDay(selectedDay);
      const nextIsoDate = nextDateStr.split("T")[0];

      const fetchAndMergeTasks = async () => {
        let tasks = await fetchTasksByDate(companyId, isoDate);
        if (nextIsoDate !== isoDate) {
          try {
            const nextTasks = await fetchTasksByDate(companyId, nextIsoDate);
            const existingCustIds = new Set(
              tasks.map((t) => t.customer?.customerId).filter(Boolean)
            );
            tasks = [
              ...tasks,
              ...nextTasks.filter((t) => !existingCustIds.has(t.customer?.customerId)),
            ];
          } catch (err) {
            console.warn("Could not fetch next-occurrence tasks for routeOrder:", err);
          }
        }
        return tasks;
      };

      let freshTasks = await fetchAndMergeTasks();

      // When new work orders were just created, the backend may need a moment to
      // persist the associated tasks. Retry once with a brief delay if any of the
      // newly added customers' tasks are not yet visible.
      if (pendingChanges.length > 0) {
        const newCustomerIds = new Set(pendingChanges.map((c) => c.id));
        const foundIds = new Set(
          freshTasks
            .filter(
              (t) =>
                t.employee?.employeeId === selectedEmployee.id &&
                t.taskDetails?.taskStatus !== "Cancelled"
            )
            .map((t) => t.customer?.customerId)
            .filter(Boolean)
        );
        const missingAny = [...newCustomerIds].some((id) => !foundIds.has(id));
        if (missingAny) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          freshTasks = await fetchAndMergeTasks();
        }
      }
      const relevantFreshTasks = freshTasks.filter(
        (task) =>
          task.taskDetails &&
          task.taskDetails.taskStatus !== "Cancelled" &&
          task.employee?.employeeId === selectedEmployee.id,
      );

      // Build customerId → task map from fresh data
      const taskByCustomerId = {};
      relevantFreshTasks.forEach((task) => {
        if (task.customer?.customerId) {
          taskByCustomerId[task.customer.customerId] = task;
        }
      });

      // Persist route order onto the recurrence RULES (generate-on-read), keyed by work
      // order id — which exists even for virtual/future occurrences that have no taskId yet.
      // This is the fix for: reorder silently no-oping on not-yet-materialized routes, and
      // order not surviving into future weeks. One call writes LAST_ROUTE_ORDER on each rule.
      const orderedWorkOrderIds = orderedCustomerIds
        .map((customerId) => {
          const task = taskByCustomerId[customerId];
          return task?.taskDetails?.wrkOdrId ?? null;
        })
        .filter((id) => id != null);

      if (orderedWorkOrderIds.length > 0) {
        await reorderRoute(orderedWorkOrderIds).catch((err) =>
          console.warn("Could not save route order:", err),
        );
      }

      const addedCount = pendingChanges.length;

      if (addedCount > 0) {
        // New customers were added — reload full list from DB
        await calculateCustomerLists();
      } else {
        // Pure reorder (or reorder + delete) — state is already correct, just refresh task data
        const freshTaskDataMap = {};
        relevantFreshTasks.forEach((t) => {
          if (t.customer?.customerId) freshTaskDataMap[t.customer.customerId] = t;
        });
        setActiveCustomers(
          orderedCustomerIds
            .map((id) => {
              const cust = activeCustomers.find((c) => c.id === id);
              if (!cust) return null;
              return { ...cust, taskData: freshTaskDataMap[id] || cust.taskData };
            })
            .filter(Boolean)
        );
        setTasks(relevantFreshTasks);
      }

      // Re-enforce user's intended order (DB round-trip may return stale routeOrders)
      if (orderedCustomerIds.length > 0) {
        const orderMap = new Map(orderedCustomerIds.map((id, i) => [id, i]));
        setActiveCustomers((prev) =>
          [...prev].sort((a, b) => {
            const ia = orderMap.has(a.id) ? orderMap.get(a.id) : orderedCustomerIds.length;
            const ib = orderMap.has(b.id) ? orderMap.get(b.id) : orderedCustomerIds.length;
            return ia - ib;
          })
        );
      }
      setPendingChanges([]);
      setHasReorderedSchedule(false);
      hasPendingChangesRef.current = false; // sync reset so blocker clears immediately

      let successMsg = "";
      if (addedCount > 0 && hasReorderedSchedule) {
        successMsg = `Successfully scheduled ${addedCount} new customer(s) and saved route order for ${dayName}s!`;
      } else if (addedCount > 0) {
        successMsg = `Successfully scheduled ${addedCount} new customer(s) for ${dayName}s!`;
      } else {
        successMsg = `Route order saved for ${dayName}!`;
      }
      setPopupMessage(successMsg);
      setPopupTitle("Success");
      setPopupVisible(true);
    } catch (error) {
      console.error("Error saving schedule:", error);
      setPopupMessage("Error saving schedule. Please try again.");
      setPopupTitle("Error");
      setPopupVisible(true);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateWorkOrderSuccess = async () => {
    // Reload tasks after creating a new work order
    try {
      await calculateCustomerLists();
    } catch (error) {
      console.error("Error reloading tasks:", error);
    }
  };

  const handleTemplateSaveSuccess = (message) => {
    setPopupTitle("Success");
    setPopupMessage(message);
    setPopupVisible(true);
  };

  const handleImportTemplate = (template) => {
    if (!template?.stops?.length) return;

    // Use customerList as the source of truth — it's always the full unfiltered
    // list of active company customers, regardless of current employee/day selection.
    const customerMap = Object.fromEntries(customerList.map((c) => [c.id, c]));

    const toAdd = [];
    const alreadyActive = [];
    const skippedNames = [];

    template.stops.forEach((stop) => {
      const existing = customerMap[stop.customerId];
      if (!existing) {
        skippedNames.push(
          `${stop.customerFirstName || ""} ${stop.customerLastName || ""}`.trim() || `ID ${stop.customerId}`
        );
        return;
      }
      if (activeCustomers.find((c) => c.id === stop.customerId)) {
        alreadyActive.push(existing);
      } else {
        toAdd.push(existing);
      }
    });

    if (toAdd.length === 0 && alreadyActive.length > 0) {
      setPopupTitle("Nothing to Import");
      setPopupMessage("All customers in this template are already on the active schedule.");
      setPopupVisible(true);
      return;
    }

    if (toAdd.length > 0) {
      setActiveCustomers([...activeCustomers, ...toAdd]);
      setAvailableCustomers(availableCustomers.filter((c) => !toAdd.find((a) => a.id === c.id)));
      setPendingChanges([...pendingChanges, ...toAdd.filter((c) => !pendingChanges.find((p) => p.id === c.id))]);
      hasPendingChangesRef.current = true;
    }

    const parts = [];
    if (toAdd.length > 0) parts.push(`${toAdd.length} customer(s) added`);
    if (alreadyActive.length > 0) parts.push(`${alreadyActive.length} already on this schedule`);
    if (skippedNames.length > 0) parts.push(`${skippedNames.length} not found (inactive or removed)`);

    setPopupTitle("Template Imported");
    setPopupMessage(parts.join(" · "));
    setPopupVisible(true);
  };

  // Backup-recovery: restore a saved template's routes server-side. Rebuilds
  // work orders for every (employee, day) the template covers. Used to recover
  // a corrupted route. Throws on failure so the library modal can react.
  const handleRestoreTemplate = async (template, conflictMode) => {
    try {
      const result = await applyTemplate(template.templateId, {
        companyId,
        conflictMode, // "OVERWRITE" | "MERGE"
        // Restore to the template's own stored employee/day (true recovery).
        targetEmployeeId: null,
        targetDayOfWeek: null,
        forceThisWeek: false,
      });

      // The database is now the source of truth — clear any local pending state.
      setPendingChanges([]);
      setHasReorderedSchedule(false);
      hasPendingChangesRef.current = false;

      try {
        // Full refresh — the server created new work orders, so refetch
        // everything (customers, employees, work orders) rather than rebuilding
        // from the now-stale local work-order state.
        await loadData();
      } catch (reloadErr) {
        console.warn("Could not reload schedule after restore:", reloadErr);
      }

      // Explicitly refresh the visible schedule after loadData
      if (selectedEmployee || selectedDay !== "") {
        try {
          await calculateCustomerLists();
        } catch (refreshErr) {
          console.warn("Could not refresh schedule view after restore:", refreshErr);
        }
      }

      const parts = [`${result.stopsRestored ?? 0} stop(s) restored`];
      if (result.workOrdersCancelled > 0) parts.push(`${result.workOrdersCancelled} replaced`);
      if (result.employeesAffected > 0) parts.push(`${result.employeesAffected} employee(s)`);
      if (result.daysAffected > 0) parts.push(`${result.daysAffected} day(s)`);
      if (result.skippedCount > 0) parts.push(`${result.skippedCount} skipped`);

      let msg = parts.join(" · ");
      if (result.skipped && result.skipped.length > 0) {
        msg += `\n\nSkipped:\n• ${result.skipped.join("\n• ")}`;
      }
      setPopupTitle("Schedule Restored");
      setPopupMessage(msg);
      setPopupVisible(true);
    } catch (err) {
      console.error("Restore failed:", err);
      setPopupTitle("Error");
      setPopupMessage("Failed to restore the template. Please try again.");
      setPopupVisible(true);
      throw err;
    }
  };

  // Apply an explicit, user-edited set of routes from the restore staging view.
  const handleApplyRoutes = async (routes, conflictMode = "OVERWRITE", reassignmentActions = []) => {
    try {
      const result = await applyRoutes({ companyId, forceThisWeek: false, routes, conflictMode, reassignmentActions });

      setPendingChanges([]);
      setHasReorderedSchedule(false);
      hasPendingChangesRef.current = false;

      try {
        await loadData();
      } catch (reloadErr) {
        console.warn("Could not reload schedule after restore:", reloadErr);
      }

      // Explicitly refresh the visible schedule — don't wait for the useEffect chain
      // which fires asynchronously and leaves a stale-empty window after restore.
      if (selectedEmployee || selectedDay !== "") {
        try {
          await calculateCustomerLists();
        } catch (refreshErr) {
          console.warn("Could not refresh schedule view after restore:", refreshErr);
        }
      }

      const restored = result.stopsRestored ?? 0;
      const skipped = result.skippedCount ?? 0;

      if (restored === 0 && skipped > 0) {
        // Nothing was restored — all customers were skipped (inactive / no pool)
        let msg = `No stops were restored. ${skipped} customer(s) could not be applied:`;
        if (result.skipped && result.skipped.length > 0) {
          msg += `\n• ${result.skipped.slice(0, 10).join("\n• ")}`;
          if (result.skipped.length > 10) msg += `\n• … and ${result.skipped.length - 10} more`;
        }
        setPopupTitle("Restore Warning");
        setPopupMessage(msg);
        setPopupVisible(true);
        return;
      }

      const parts = [`${restored} stop(s) restored`];
      if (result.workOrdersCancelled > 0) parts.push(`${result.workOrdersCancelled} replaced`);
      if (result.employeesAffected > 0) parts.push(`${result.employeesAffected} employee(s)`);
      if (result.daysAffected > 0) parts.push(`${result.daysAffected} day(s)`);
      if (skipped > 0) parts.push(`${skipped} skipped`);

      let msg = parts.join(" · ");
      if (result.skipped && result.skipped.length > 0) {
        msg += `\n\nSkipped:\n• ${result.skipped.join("\n• ")}`;
      }
      setPopupTitle("Schedule Restored");
      setPopupMessage(msg);
      setPopupVisible(true);
    } catch (err) {
      console.error("Apply routes failed:", err);
      setPopupTitle("Error");
      setPopupMessage("Failed to restore the schedule. Please try again.");
      setPopupVisible(true);
      throw err;
    }
  };

  const canDragDrop = selectedEmployee && selectedDay !== "";
  const hasPendingChanges = pendingChanges.length > 0 || hasReorderedSchedule;

  return (
    <Box className="employee-scheduling-container">
      <Paper
        elevation={3}
        sx={{ padding: "30px", maxWidth: "1200px", margin: "auto" }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", marginBottom: "10px" }}
            >
              Employee Scheduling
            </Typography>
            <Typography
              variant="body2"
              sx={{ marginBottom: "20px", color: "red", fontWeight: "bold" }}
            >
              Select an employee and day of the week, then drag customers to assign
              them to the schedule. Routes are visible immediately and repeat automatically every week.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
            onClick={() => setCreateWorkOrderModalOpen(true)}
            size="small"
            sx={{
              backgroundColor: "#1d5ca7",
              color: "#fff",
              fontWeight: "bold",
              marginTop: "-10px",
              padding: "4px 8px",
              fontSize: "0.75rem",
              minWidth: "auto",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#164a8a",
              },
            }}
          >
            Create Route
          </Button>
        </Box>

        {/* Filters */}
        <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={employeeList}
              getOptionLabel={(option) => {
                // Check if there are other employees with the same name
                const duplicates = employeeList.filter(emp => emp.name === option.name);
                if (duplicates.length > 1 && option.phone) {
                  // Show phone number if there are duplicates
                  return `${option.name} (${option.phone})`;
                }
                return option.name;
              }}
              value={selectedEmployee}
              onChange={(e, newValue) => setSelectedEmployee(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee"
                  variant="outlined"
                  inputProps={{ ...params.inputProps, autoCorrect: "off", autoComplete: "off", spellCheck: false }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="day-select-label">Select Day of Week</InputLabel>
              <Select
                labelId="day-select-label"
                label="Select Day of Week"
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {DAYS_OF_WEEK.map((day) => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ marginBottom: "20px" }} />

        {loading ? (
          <Box display="flex" justifyContent="center" padding="40px">
            <CircularProgress />
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            autoScroll={{ layoutShiftCompensation: false }}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Grid container spacing={3}>
              {/* Available Customers List */}
              <Grid item xs={12} md={6}>
                <Paper
                  className="drop-zone"
                  elevation={2}
                  sx={{
                    padding: "15px",
                    height: "500px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ marginBottom: "15px", fontWeight: "bold" }}
                  >
                    All Customers
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Customer"
                    variant="outlined"
                    value={searchCustomer}
                    onChange={(e) => setSearchCustomer(e.target.value)}
                    sx={{ marginBottom: "15px" }}
                    inputProps={{ autoCorrect: "off", autoComplete: "off", spellCheck: false }}
                  />
                  <Box
                    sx={{ overflowY: "auto", overflowX: "hidden", flexGrow: 1 }}
                  >
                    <DroppableContainer id="available-customers">
                      <SortableContext
                        items={availableCustomers
                          .filter((c) =>
                            (() => { const q = searchCustomer.toLowerCase(); return !q || c.name.toLowerCase().includes(q) || c.firstName.includes(q) || c.lastName.includes(q); })()
                          )
                          .map((c) => c.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {availableCustomers.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            All customers are scheduled
                          </Typography>
                        ) : availableCustomers.filter((c) =>
                            (() => { const q = searchCustomer.toLowerCase(); return !q || c.name.toLowerCase().includes(q) || c.firstName.includes(q) || c.lastName.includes(q); })()
                          ).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No customers found
                          </Typography>
                        ) : (
                          availableCustomers
                            .filter((c) =>
                              (() => { const q = searchCustomer.toLowerCase(); return !q || c.name.toLowerCase().includes(q) || c.firstName.includes(q) || c.lastName.includes(q); })()
                            )
                            .map((customer) => (
                              <DraggableCustomerCard
                                key={customer.id}
                                customer={customer}
                                isActive={false}
                                isDragEnabled={canDragDrop}
                                onAdd={handleAddCustomer}
                              />
                            ))
                        )}
                      </SortableContext>
                    </DroppableContainer>
                  </Box>
                </Paper>
              </Grid>

              {/* Active Schedule List */}
              <Grid item xs={12} md={6}>
                <Paper
                  className="drop-zone"
                  elevation={2}
                  sx={{
                    padding: "15px",
                    height: "500px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ marginBottom: "15px", fontWeight: "bold" }}
                  >
                    Active Schedule
                    {(selectedEmployee || selectedDay !== "") && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        {" - "}
                        {selectedDay !== "" &&
                          DAYS_OF_WEEK.find((d) => d.value === selectedDay)
                            ?.label}
                        {selectedEmployee && selectedDay !== "" && " / "}
                        {selectedEmployee && selectedEmployee.name}
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ marginBottom: "10px", fontWeight: "500", color: "#002244" }}
                  >
                    Total Customers: {activeCustomers.length}
                  </Typography>
                  <Box
                    sx={{ overflowY: "auto", overflowX: "hidden", flexGrow: 1 }}
                  >
                    <DroppableContainer id="active-customers">
                      <SortableContext
                        items={activeCustomers.map((c) => c.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {!selectedEmployee && selectedDay === "" ? (
                          <Typography variant="body2" color="text.secondary">
                            Please select an employee and/or a day of the week
                            to view the active schedule.
                          </Typography>
                        ) : activeCustomers.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No customers scheduled
                            {canDragDrop
                              ? ". Drag customers here to assign them"
                              : ""}
                            .
                          </Typography>
                        ) : (
                          <>
                            {!canDragDrop && (
                              <Typography
                                variant="body2"
                                color="warning.main"
                                sx={{
                                  marginBottom: "10px",
                                  padding: "8px",
                                  backgroundColor: "#fff3e0",
                                  borderRadius: "4px",
                                }}
                              >
                                Select both an employee and a day to enable
                                drag-and-drop scheduling.
                              </Typography>
                            )}
                            {activeCustomers.map((customer) => (
                              <DraggableCustomerCard
                                key={customer.id}
                                customer={customer}
                                isActive={true}
                                onRemove={handleRemoveCustomer}
                                showDay={selectedEmployee && selectedDay === ""}
                                showEmployeeName={
                                  !selectedEmployee && selectedDay !== ""
                                }
                                isDragEnabled={canDragDrop}
                              />
                            ))}
                          </>
                        )}
                      </SortableContext>
                    </DroppableContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </DndContext>
        )}

        {/* Template Buttons */}
        {canManageTemplates && (
          <Box sx={{ marginTop: "20px" }}>
            <Box
              sx={{
                backgroundColor: "#fff8e1",
                border: "1px solid #ffe082",
                borderRadius: "6px",
                padding: "10px 14px",
                marginBottom: "12px",
              }}
            >
              <Typography variant="body2" sx={{ color: "#5d4037", lineHeight: 1.6 }}>
                <strong>Save Recovery Template</strong> — takes a snapshot of the current schedule exactly as it is right now. No data is deleted. Use this as a safety backup so you can restore the schedule back to this exact state at any point in the future.
                <br />
                <strong>Import Template</strong> — replaces the current active schedule with a previously saved template. Your existing route will be overwritten with the template's customers and order, but all historical task records and completed service history are preserved and never deleted.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={() => setTemplateLibraryOpen(true)}
              sx={{ borderColor: "#002244", color: "#002244" }}
            >
              Import Template
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSaveTemplateConfirmOpen(true)}
              disabled={activeCustomers.length === 0}
              sx={{ borderColor: "#002244", color: "#002244" }}
            >
              Save Recovery Template
            </Button>
            </Box>
          </Box>
        )}

        {/* Save Button */}
        {canDragDrop && (
          <Box sx={{ marginTop: "20px", textAlign: "center" }}>
            {hasPendingChanges && selectedEmployee && selectedDay !== "" && (
              <Typography
                variant="body2"
                color="primary"
                sx={{ marginBottom: "10px" }}
              >
                {activeCustomers.length} customer(s) to be scheduled
              </Typography>
            )}
            {hasPendingChanges && (!selectedEmployee || selectedDay === "") && (
              <Typography
                variant="body2"
                color="warning.main"
                sx={{ marginBottom: "10px" }}
              >
                Please select both an employee and a day of the week to save
                changes
              </Typography>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={handleSaveButtonClick}
              disabled={
                !hasPendingChanges ||
                saving ||
                !selectedEmployee ||
                selectedDay === ""
              }
              sx={{ paddingX: "40px" }}
            >
              {saving ? (
                <CircularProgress size={24} />
              ) : (
                "Save Schedule Changes"
              )}
            </Button>
          </Box>
        )}

        {/* Success/Error Popup */}
        <PopupGeneric
          isVisible={isPopupVisible}
          onClose={async () => {
            const wasRestore = popupTitle === "Schedule Restored";
            setPopupVisible(false);
            if (wasRestore) {
              try { await loadData(); } catch (_) {}
              if (selectedEmployee || selectedDay !== "") {
                try { await calculateCustomerLists(); } catch (_) {}
              }
            }
          }}
          title={popupTitle}
          content={<p>{popupMessage}</p>}
          actions={
            <button
              onClick={async () => {
                const wasRestore = popupTitle === "Schedule Restored";
                setPopupVisible(false);
                if (wasRestore) {
                  try { await loadData(); } catch (_) {}
                  if (selectedEmployee || selectedDay !== "") {
                    try { await calculateCustomerLists(); } catch (_) {}
                  }
                }
              }}
              style={{
                marginTop: "10px",
                padding: "5px 10px",
                borderRadius: "5px",
                backgroundColor: "#003366",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              OK
            </button>
          }
        />

        {/* Delete Confirmation Popup */}
        <PopupGeneric
          isVisible={isDeletePopupVisible}
          onClose={onCancelDelete}
          title="Remove from Route"
          content={
            <p>
              Are you sure you want to delete from this route?
            </p>
          }
          actions={
            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                onClick={onConfirmDelete}
                style={{
                  padding: "8px 16px",
                  borderRadius: "5px",
                  backgroundColor: "#1d5ca7",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Yes
              </button>
              <button
                onClick={onCancelDelete}
                style={{
                  padding: "8px 16px",
                  borderRadius: "5px",
                  backgroundColor: "#8f0c03",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                No
              </button>
            </div>
          }
        />

        {/* Navigation Blocker Confirmation Popup */}
        <PopupGeneric
          isVisible={isNavigationBlockerVisible}
          onClose={handleNavigationBlockerCancel}
          title="Unsaved Changes"
          content={
            <div>
              <p>You have unsaved schedule changes.</p>
              <p>Please save your changes before leaving this page.</p>
            </div>
          }
          actions={
            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                onClick={handleNavigationBlockerSave}
                disabled={saving || !selectedEmployee || selectedDay === ""}
                style={{
                  padding: "8px 16px",
                  borderRadius: "5px",
                  backgroundColor: (!selectedEmployee || selectedDay === "") ? "#cccccc" : "#1d5ca7",
                  color: "#fff",
                  border: "none",
                  cursor: (!selectedEmployee || selectedDay === "") ? "not-allowed" : "pointer",
                }}
                title={(!selectedEmployee || selectedDay === "") ? "Please select both employee and day to save" : "Save changes and continue"}
              >
                {saving ? "Saving..." : "Save and Continue"}
              </button>
              <button
                onClick={handleNavigationBlockerDiscard}
                style={{
                  padding: "8px 16px",
                  borderRadius: "5px",
                  backgroundColor: "#8f0c03",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Don't Save
              </button>
            </div>
          }
        />

        {/* Duplicate Assignment Warning Popup */}
        <PopupGeneric
          isVisible={isDuplicateWarningVisible}
          onClose={onCancelDuplicateAssignment}
          title="Customer Already Scheduled"
          content={
            <div>
              <p>
                <strong>{customerToAdd?.name}</strong> is already on{" "}
                <strong>{existingEmployeeName}</strong>'s{" "}
                <strong>{selectedDay !== "" ? DAYS_OF_WEEK.find((d) => d.value === selectedDay)?.label : ""}</strong>{" "}
                route. How would you like to proceed?
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
                <div style={{ padding: "10px 14px", backgroundColor: "#fff8e1", borderRadius: "6px",
                              borderLeft: "4px solid #f57c00", fontSize: "0.85rem", lineHeight: "1.5" }}>
                  <strong style={{ color: "#e65100" }}>This week only</strong> — adds{" "}
                  <strong>{customerToAdd?.name}</strong> to{" "}
                  <strong>{selectedEmployee?.name}</strong>'s route for this week only.{" "}
                  <strong>{existingEmployeeName}</strong>'s active schedule is unchanged (they return next week).
                </div>
                <div style={{ padding: "10px 14px", backgroundColor: "#fff5f5", borderRadius: "6px",
                              borderLeft: "4px solid #c62828", fontSize: "0.85rem", lineHeight: "1.5" }}>
                  <strong style={{ color: "#c62828" }}>Permanent</strong> — moves{" "}
                  <strong>{customerToAdd?.name}</strong> to{" "}
                  <strong>{selectedEmployee?.name}</strong> permanently and{" "}
                  <strong>removes</strong> them from{" "}
                  <strong>{existingEmployeeName}</strong>'s active schedule.
                </div>
              </div>
            </div>
          }
          actions={
            <>
              <Button
                variant="outlined"
                onClick={onCancelDuplicateAssignment}
                sx={{ borderColor: "#bbb", color: "#555", whiteSpace: "nowrap", "&:hover": { borderColor: "#888", backgroundColor: "#f5f5f5" } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={onTemporaryAssign}
                sx={{ backgroundColor: "#e65100", whiteSpace: "nowrap", "&:hover": { backgroundColor: "#bf360c" } }}
              >
                This week only
              </Button>
              <Button
                variant="contained"
                onClick={onRequestPermanentConfirm}
                sx={{ backgroundColor: "#002244", whiteSpace: "nowrap" }}
              >
                Permanent
              </Button>
            </>
          }
        />

        {/* Permanent move — second-step confirmation (also auto-saves) */}
        <PopupGeneric
          isVisible={isPermanentConfirmVisible}
          onClose={onCancelPermanentConfirm}
          title="Confirm Permanent Move"
          content={
            <div>
              <div style={{ padding: "10px 14px", backgroundColor: "#fff5f5", borderRadius: "6px",
                            borderLeft: "4px solid #c62828", fontSize: "0.9rem", lineHeight: "1.55" }}>
                <p style={{ margin: 0 }}>
                  This will <strong>permanently move {customerToAdd?.name}</strong> to{" "}
                  <strong>{selectedEmployee?.name}</strong> and <strong>remove them from{" "}
                  {existingEmployeeName}</strong>'s active schedule.
                </p>
                <p style={{ margin: "8px 0 0" }}>
                  The change will be <strong>saved immediately</strong> — you won't need to click Save.
                </p>
              </div>
            </div>
          }
          actions={
            <>
              <Button
                variant="outlined"
                onClick={onCancelPermanentConfirm}
                sx={{ borderColor: "#bbb", color: "#555", whiteSpace: "nowrap", "&:hover": { borderColor: "#888", backgroundColor: "#f5f5f5" } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={onConfirmDuplicateAssignment}
                sx={{ backgroundColor: "#c62828", whiteSpace: "nowrap", "&:hover": { backgroundColor: "#b71c1c" } }}
              >
                Confirm &amp; Save
              </Button>
            </>
          }
        />

        {/* Same Employee Other Day Conflict Popup */}
        <PopupGeneric
          isVisible={isSameDayConflictVisible}
          onClose={onCancelSameDayConflict}
          title="Already Scheduled This Week"
          content={
            <div>
              <p>
                <strong>{customerToAdd?.name}</strong> is already scheduled
                with <strong>{selectedEmployee?.name}</strong> on{" "}
                <strong>{conflictDayLabel}</strong>.
              </p>
              <p>
                Do you want to also schedule them on{" "}
                <strong>
                  {selectedDay !== ""
                    ? DAYS_OF_WEEK.find((d) => d.value === selectedDay)?.label
                    : ""}
                </strong>
                ?
              </p>
            </div>
          }
          actions={
            <>
              <Button
                variant="outlined"
                onClick={onCancelSameDayConflict}
                sx={{ borderColor: "#bbb", color: "#555", "&:hover": { borderColor: "#888", backgroundColor: "#f5f5f5" } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={onConfirmSameDayConflict}
                sx={{ backgroundColor: "#002244" }}
              >
                Yes, Add
              </Button>
            </>
          }
        />

        {/* Create Work Order Modal */}
        <CreateWorkOrderModal
          open={isCreateWorkOrderModalOpen}
          onClose={() => setCreateWorkOrderModalOpen(false)}
          onSuccess={handleCreateWorkOrderSuccess}
        />

        {/* Save Recovery Template Confirmation Popup */}
        <PopupGeneric
          isVisible={isSaveTemplateConfirmOpen}
          onClose={() => setSaveTemplateConfirmOpen(false)}
          title="Save Recovery Template"
          content={
            <div>
              <p style={{ marginBottom: "10px" }}>
                Are you satisfied with the current schedule? This will save a snapshot of all active routes exactly as they appear right now.
              </p>
              <p style={{ margin: 0, color: "#666", fontSize: "13px" }}>
                You can restore this template at any time to bring the schedule back to this state.
              </p>
            </div>
          }
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() => setSaveTemplateConfirmOpen(false)}
                sx={{ borderColor: "#bbb", color: "#555", "&:hover": { borderColor: "#888", backgroundColor: "#f5f5f5" } }}
              >
                No, Go Back
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setSaveTemplateConfirmOpen(false);
                  setSaveTemplateModalOpen(true);
                }}
                sx={{ backgroundColor: "#002244" }}
              >
                Yes, Save It
              </Button>
            </>
          }
        />

        {/* Save Template Modal */}
        <SaveTemplateModal
          open={isSaveTemplateModalOpen}
          onClose={() => setSaveTemplateModalOpen(false)}
          onSuccess={handleTemplateSaveSuccess}
          activeCustomers={activeCustomers}
          companyId={companyId}
          createdByEmpId={selectedEmployee?.id ?? userDetails?.employeeId ?? null}
          origEmpId={selectedEmployee?.id ?? null}
          suggestedDay={selectedDay !== "" ? selectedDay : null}
          selectedEmployeeName={selectedEmployee?.name ?? null}
        />

        {/* Template Library Modal */}
        <TemplateLibraryModal
          open={isTemplateLibraryOpen}
          onClose={() => setTemplateLibraryOpen(false)}
          onImport={handleImportTemplate}
          onRestore={handleRestoreTemplate}
          onApplyRoutes={handleApplyRoutes}
          companyId={companyId}
          employeeId={userDetails?.employeeId ?? null}
          canManage={canManageTemplates}
        />
      </Paper>
    </Box>
  );
};

export default EmployeeScheduling;
